// Prüfer für Aufbau- und Buszyklus-Aufgaben -- Gegenstück zu buslab/aufbau.py und buslab/zyklus.py.
// Beide müssen tests/fixtures/aufbau.json und zyklus.json bestehen (siehe model.test.ts).

export type Bit = 0 | 1;
export type PortArt = "adresse" | "daten" | "steuer";

export interface Port { id: string; art: PortArt; schnittstelle: "" | "befehl" | "daten" }

export interface Baustein {
  id: string;
  art: "cpu" | "speicher" | "ea";
  name: string;
  pfad?: "" | "befehl" | "daten"; // an welche CPU-Seite der Baustein gehört
  woerter?: number;
  bits?: number;
  datenbus?: number; // Bit pro Zugriff, wenn die CPU mehrere Speicherzellen auf einmal holt
  ports: Port[];
}

export type Ziel =
  | { art: "speicher"; adresse: string; wert: string }
  | { art: "register" | "ea"; wert: string };

interface TaskBase { id: string; titel: string; text: string; hinweise: string[]; block: string | null; ea: boolean }
export interface AufbauTask extends TaskBase { typ: "aufbau"; architektur: "von-neumann" | "harvard"; bausteine: Baustein[] }
export interface ZyklusTask extends TaskBase {
  typ: "buszyklus"; woerter: number; bits: number; abits: number; inhalt: string[]; ziel: Ziel[]; par: number;
  /** D-Schalter gesperrt: geschrieben wird nur der Registerinhalt (erst lesen, dann schreiben). */
  daten_aus_register?: boolean;
}
export type Task = AufbauTask | ZyklusTask;

export interface Problem { code: string; ref: string | null; message: string }

// ---------------------------------------------------------------- Aufbau

export interface Bus { ports: string[]; breite?: number | null }
export interface AufbauLoesung { busse: Bus[] }
export interface AufbauResult { solved: boolean; problems: Problem[] }

export const ARTEN: Record<PortArt, string> = { adresse: "Adressbus", daten: "Datenbus", steuer: "Steuerbus" };
export const SCHNITTSTELLEN: Record<string, string> = {
  befehl: "Befehlsseite (Steuerwerk)", daten: "Datenseite (Rechenwerk)",
};
const KURZ: Record<PortArt, string> = { adresse: "Adress", daten: "Daten", steuer: "Steuer" };
const FRAGE: Record<PortArt, string> = {
  adresse: "woher weiß er, welche Speicherzelle gemeint ist?",
  daten: "wie kommen die Daten hin und zurück?",
  steuer: "woher weiß er, ob gelesen oder geschrieben wird -- und ob er gemeint ist?",
};

/** Anzahl Adressleitungen für `woerter` Speicherzellen (Zweierpotenz ≥ 2). */
export const adressbits = (woerter: number): number => (woerter - 1).toString(2).length;

const problem = (code: string, ref: string | null, message: string): Problem => ({ code, ref, message });

export function checkAufbau(bausteine: Baustein[], loesung: AufbauLoesung): AufbauResult {
  const ports = new Map<string, [Baustein, Port]>();
  for (const b of bausteine) for (const p of b.ports) ports.set(`${b.id}.${p.id}`, [b, p]);
  const busse = loesung?.busse;
  if (!Array.isArray(busse)) return { solved: false, problems: [problem("ungueltig", null, "Keine Busse übergeben")] };

  const problems: Problem[] = [];
  const belegt = new Map<string, number>();
  const netze: [number, string[], number | null][] = [];
  for (const [i, bus] of busse.entries()) {
    const refs = bus?.ports;
    const breite = bus?.breite ?? null;
    if (!Array.isArray(refs) || refs.some(r => !ports.has(r)) || new Set(refs).size !== refs.length
      || (breite !== null && (!Number.isInteger(breite) || breite < 1 || breite > 64))) {
      return { solved: false, problems: [problem("ungueltig", `bus:${i}`, "Ungültiger Bus")] };
    }
    for (const r of refs) {
      if (belegt.has(r)) return { solved: false, problems: [problem("ungueltig", r, `Anschluss ${r} hängt an zwei Bussen`)] };
      belegt.set(r, i);
    }
    if (refs.length >= 2) netze.push([i, refs, breite]);
  }

  const angeschlossen = new Set<string>();
  const falschGemeldet = new Set<string>(); // falscher_pfad nur einmal je Baustein
  for (const [i, refs, breite] of netze) {
    for (const r of refs) angeschlossen.add(r);
    const ref = `bus:${i}`;
    const arten = new Set(refs.map(r => ports.get(r)![1].art));
    if (arten.size > 1) {
      const kurz = (Object.keys(ARTEN) as PortArt[]).filter(a => arten.has(a)).map(a => KURZ[a]);
      const namen = kurz.slice(0, -1).join("-, ") + "- und " + kurz[kurz.length - 1];
      problems.push(problem("typ_gemischt", ref, `Ein Bus verbindet ${namen}anschlüsse -- jeder Bus hat nur eine Aufgabe.`));
      continue;
    }
    const art = [...arten][0];
    const cpu = refs.filter(r => ports.get(r)![0].art === "cpu");
    const geraete = refs.filter(r => ports.get(r)![0].art !== "cpu");
    if (cpu.length === 0) {
      problems.push(problem("ohne_cpu", ref, `Dieser ${ARTEN[art]} hängt nicht an der CPU -- wer legt die Signale an?`));
      continue;
    }
    if (cpu.length > 1) {
      problems.push(problem("schnittstellen_verbunden", ref,
        "Befehls- und Datenseite der CPU sind zusammengeschlossen -- dann wäre es wieder ein "
        + "gemeinsamer Bus wie bei Von Neumann."));
      continue;
    }
    const schnittstelle = ports.get(cpu[0])![1].schnittstelle;
    for (const r of geraete) {
      const b = ports.get(r)![0];
      if ((b.pfad ?? "") !== schnittstelle) {
        if (!falschGemeldet.has(b.id)) {
          falschGemeldet.add(b.id);
          const soll = SCHNITTSTELLEN[b.pfad ?? ""] ?? "";
          problems.push(problem("falscher_pfad", r, `${b.name} gehört an die ${soll} der CPU.`));
        }
        continue;
      }
      if (art === "steuer") continue;
      const soll = art === "adresse" ? adressbits(b.woerter!) : b.datenbus ?? b.bits!;
      if (breite !== soll) {
        const ist = breite ?? 0;
        const msg = art === "adresse" && b.woerter! > 65536 // Riesenzahlen nur als Zweierpotenz
          ? `${b.name}: 2^${soll} Speicherzellen brauchen genau ${soll} Adressleitungen, eingestellt sind ${ist}.`
          : art === "adresse"
          ? `${b.name}: ${b.woerter} Speicherzellen brauchen genau ${soll} Adressleitungen (2^${soll} = ${2 ** soll}), eingestellt sind ${ist}.`
          : b.datenbus
          ? `${b.name}: Die CPU holt ${soll / b.bits!} Speicherzellen zu je ${b.bits} Bit auf einmal, der Datenbus braucht also ${soll} Leitungen, eingestellt sind ${ist}.`
          : `${b.name}: Eine Speicherzelle hat ${b.bits} Bit, der Datenbus braucht also ${soll} Leitungen, eingestellt sind ${ist}.`;
        problems.push(problem(art === "adresse" ? "adressbus_breite" : "datenbus_breite", r, msg));
      }
    }
  }

  for (const [ref, [b, p]] of ports) {
    if (angeschlossen.has(ref)) continue;
    const msg = b.art === "cpu"
      ? `Der ${KURZ[p.art]}anschluss der CPU${p.schnittstelle ? "-" + SCHNITTSTELLEN[p.schnittstelle] : ""} ist noch frei.`
      : `${b.name} hat keinen ${ARTEN[p.art]} -- ${FRAGE[p.art]}`;
    problems.push(problem("offen", ref, msg));
  }
  return { solved: problems.length === 0, problems };
}

// ---------------------------------------------------------------- Buszyklus

/** Belegung aller Leitungen in einem Takt (Bitstrings, höchstwertiges Bit zuerst). RW: 1 = lesen. */
export interface Signal { A: string; D?: string; RW: Bit; CS_S?: Bit; CS_E?: Bit }
/** Rechnung im Register ohne Bus: +1 bzw. ×2 (alle Bits eine Stelle nach links), modulo 2^bits. */
export type Op = "inc" | "shl";
export interface Rechnen { op: Op }
/** Ein Schritt der Lösung: Bustakt oder Rechnung im Register (beide zählen als Takt). */
export type Schritt = Signal | Rechnen;
export const istRechnen = (s: Schritt): s is Rechnen => "op" in s;
export interface Zustand { speicher: string[]; register: string | null; ea: string | null }
export type Ereignis =
  | { typ: "niemand" | "nur_ausgabe" }
  | { typ: "lesen"; adresse: string; wert: string }
  | { typ: "schreiben"; adresse: string; wert: string; ziele: ("speicher" | "ea")[] }
  | { typ: "rechnen"; op: Op; vorher: string; wert: string; ueberlauf: boolean };

export interface ZyklusResult {
  solved: boolean;
  problems: Problem[];
  zustand: Zustand | null;
  ereignisse: Ereignis[];
  takte: number;
  par_erreicht: boolean;
}

export const MAX_TAKTE = 50;

const isBits = (s: unknown, n: number): s is string => typeof s === "string" && s.length === n && /^[01]*$/.test(s);
const isFlag = (v: unknown): v is Bit => v === 0 || v === 1;

export const start = (task: ZyklusTask): Zustand => ({ speicher: [...task.inhalt], register: null, ea: null });

function rechnen(task: ZyklusTask, zustand: Zustand, schritt: Rechnen): [Zustand, Ereignis] | null {
  const { op } = schritt, reg = zustand.register;
  if (Object.keys(schritt).length !== 1 || (op !== "inc" && op !== "shl") || reg === null) return null;
  const n = task.bits;
  const wert = op === "inc" ? parseInt(reg, 2) + 1 : parseInt(reg, 2) * 2;
  const neu: Zustand = { speicher: [...zustand.speicher], register: (wert % 2 ** n).toString(2).padStart(n, "0"), ea: zustand.ea };
  return [neu, { typ: "rechnen", op, vorher: reg, wert: neu.register!, ueberlauf: wert >= 2 ** n }];
}

/** Führt einen Takt aus. Liefert [neuer Zustand, Ereignis] oder null bei ungültigem Signal. */
export function takt(task: ZyklusTask, zustand: Zustand, schritt: Schritt): [Zustand, Ereignis] | null {
  if (typeof schritt !== "object" || schritt === null) return null;
  if (istRechnen(schritt)) return rechnen(task, zustand, schritt);
  const signal = schritt;
  const { A: a, D: d, RW: rw } = signal;
  const csS = signal.CS_S ?? 0, csE = signal.CS_E ?? 0;
  if (!isBits(a, task.abits) || !isFlag(rw) || !isFlag(csS) || !isFlag(csE)
    || (csE && !task.ea) || (rw === 0 && !isBits(d, task.bits))) return null;
  if (rw === 0 && (csS || csE) && task.daten_aus_register && d !== zustand.register) return null;
  const neu: Zustand = { speicher: [...zustand.speicher], register: zustand.register, ea: zustand.ea };
  const adr = parseInt(a, 2);
  if (!csS && !csE) return [neu, { typ: "niemand" }];
  if (rw === 1) {
    if (!csS) return [neu, { typ: "nur_ausgabe" }];
    neu.register = neu.speicher[adr];
    return [neu, { typ: "lesen", adresse: a, wert: neu.register }];
  }
  const ziele: ("speicher" | "ea")[] = [];
  if (csS) { neu.speicher[adr] = d!; ziele.push("speicher"); }
  if (csE) { neu.ea = d!; ziele.push("ea"); }
  return [neu, { typ: "schreiben", adresse: a, wert: d!, ziele }];
}

export function checkZyklus(task: ZyklusTask, loesung: { takte: Schritt[] }): ZyklusResult {
  const takte = loesung?.takte;
  const result: ZyklusResult = { solved: false, problems: [], zustand: null, ereignisse: [], takte: 0, par_erreicht: false };
  if (!Array.isArray(takte) || takte.length > MAX_TAKTE) {
    result.problems.push(problem("ungueltig", null, "Ungültige Takt-Liste"));
    return result;
  }
  if (takte.length === 0) {
    result.problems.push(problem("kein_takt", null, "Noch kein Takt gegeben."));
    return result;
  }

  let zustand = start(task);
  for (const [i, signal] of takte.entries()) {
    const schritt = takt(task, zustand, signal);
    if (schritt === null) {
      result.problems.push(problem("ungueltig", `takt:${i}`, `Takt ${i + 1} ist ungültig`));
      return result;
    }
    zustand = schritt[0];
    result.ereignisse.push(schritt[1]);
  }
  result.zustand = zustand;
  result.takte = takte.length;

  const problems = result.problems;
  const zielAdressen = new Set<string>();
  for (const z of task.ziel) {
    if (z.art === "speicher") {
      zielAdressen.add(z.adresse);
      const ist = zustand.speicher[parseInt(z.adresse, 2)];
      if (ist !== z.wert) problems.push(problem("ziel_speicher", z.adresse, `In Speicherzelle ${z.adresse} steht ${ist}, soll ${z.wert}.`));
    } else if (z.art === "register") {
      if (zustand.register !== z.wert) {
        problems.push(problem("ziel_register", null, `Im Register der CPU steht ${zustand.register ?? "noch nichts"}, soll ${z.wert}.`));
      }
    } else if (zustand.ea !== z.wert) {
      problems.push(problem("ziel_ea", null, `Die Ausgabe zeigt ${zustand.ea ?? "noch nichts"}, soll ${z.wert}.`));
    }
  }

  task.inhalt.forEach((vorher, adr) => {
    const a = adr.toString(2).padStart(task.abits, "0");
    const nachher = zustand.speicher[adr];
    if (!zielAdressen.has(a) && vorher !== nachher) {
      problems.push(problem("nebenwirkung", a, `Speicherzelle ${a} wurde versehentlich überschrieben (${vorher} → ${nachher}).`));
    }
  });
  if (!task.ziel.some(z => z.art === "ea") && zustand.ea !== null) {
    problems.push(problem("nebenwirkung", "ea", "Das E/A-Werk hat etwas ausgegeben, obwohl es nicht gemeint war."));
  }

  result.solved = problems.length === 0;
  result.par_erreicht = result.solved && result.takte <= task.par;
  return result;
}

/** Prüft eine Lösung (Aufbau: {busse}, Buszyklus: {takte}). */
export function check(task: Task, loesung: AufbauLoesung | { takte: Schritt[] }): AufbauResult | ZyklusResult {
  return task.typ === "aufbau"
    ? checkAufbau(task.bausteine, loesung as AufbauLoesung)
    : checkZyklus(task, loesung as { takte: Schritt[] });
}
